'use client';

import { Button } from '@core-components/Button';
import { cn } from '@core-utils/cn';
import Image from 'next/image';
import { useEffect, useRef } from 'react';

import closeIcon from './close_icon.svg';
import { modalStyles as styles } from './styles';
import { Dialog } from './types';

type HeaderProps = Dialog['header'];
type FooterProps = Dialog['footer'];

type ModalProps = {
    open: boolean;
    size: Dialog['size'];
    children?: React.ReactNode;
    onClose?: () => void;
    header?: HeaderProps;
    footer?: FooterProps;
};

export function DialogModal(props: ModalProps) {
    let { open, children, header, footer, size } = props;

    header = headerConfig(header);

    const modalRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        return () => {
            enableBackgroundScroll();
        };
    }, []);

    useEffect(() => {
        if (open) {
            disableBackgroundScroll();
            modalRef.current?.showModal();
        } else {
            enableBackgroundScroll();
            modalRef.current?.close();
        }
    }, [open]);

    const selfClose = () => {
        enableBackgroundScroll();
        props?.onClose?.();
    };

    const headerSlot = header?.slot;
    const footerSlot = footer?.slot;
    const showHeader = !header?.hide;
    const showFooter = !footer?.hide;

    return (
        <dialog
            ref={modalRef}
            className={cn(
                styles.blockScope,
                size &&
                    {
                        large: styles.blockScopeLarge,
                        medium: styles.blockScopeMedium,
                        small: styles.blockScopeSmall,
                    }[size],
            )}
        >
            <section>
                {showHeader ? (
                    <header className={styles.header}>
                        {headerSlot ? (
                            <>{headerSlot}</>
                        ) : (
                            <>
                                <h2>{header?.title}</h2>
                                <Button
                                    className={styles.closeButton}
                                    onClick={selfClose}
                                    aria-label="Close dialog"
                                >
                                    <Image src={closeIcon} alt="Close icon" />
                                </Button>
                            </>
                        )}
                    </header>
                ) : null}
                <main className={styles.content}>{children}</main>
                {showFooter ? (
                    <footer className={styles.footer}>{footerSlot}</footer>
                ) : null}
            </section>
            <style>
                .mdxeditor-popup-container {'{' + 'z-index: 200;' + '}'}
                .mdxeditor-toolbar {'{' + 'z-index: 0;' + '}'}
            </style>
        </dialog>
    );
}

// helpers

const headerConfig = (prop?: string | HeaderProps) => {
    let config: HeaderProps = {};
    if (!config) return {};
    if (typeof prop === 'string')
        return {
            title: prop,
        };
    return {
        ...config,
        ...prop,
    };
};

// some background scroll js

const disableBackgroundScroll = () => {
    // dependencies check
    if (!document) return;

    const scrollY =
        document.documentElement.style.getPropertyValue('--scroll-y');
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}`;
    body.style.width = '100%';
};

const enableBackgroundScroll = () => {
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    // dependencies check
    if (!document) return;
    if (!window) return;

    const body = document.body;
    const scrollY = body.style.top;
    body.style.position = '';
    body.style.top = '';
    body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
};

(function () {
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    window.addEventListener('scroll', () => {
        document.documentElement.style.setProperty(
            '--scroll-y',
            `${window.scrollY}px`,
        );
    });
})();
